import * as React from 'react';
import classNames from 'classnames';
import {Icon} from 'components/UIKit/Icon';
import {NOOP} from 'components/UIKit/utilities';
import {ListingThumbnailProps, ListingThumbnail} from '../ListingThumbnail';
import {ShopScreenShot} from '../../../../types';
import * as style from './ListingThumbnails.scss';

export interface ListingThumbnailsProps {
  images: ShopScreenShot[];
  selected: ShopScreenShot;
  altPrefix?: string;
  orientation: 'vertical' | 'horizontal';
  onThumbnailClick: (image: ShopScreenShot) => void;
}

export interface ListingThumbnailsState {
  showLeftArrow: boolean;
  showRightArrow: boolean;
  thumbsTranslate: number;
  maxThumbsTranslate: number;
}

interface ThumbnailVisibilityStatus {
  component: ListingThumbnail;
  props: ListingThumbnailProps;
  inView: boolean;
}

export function maxTranslationValue(scrollerWidth, thumbnailsWidth) {
  return Math.min(scrollerWidth - thumbnailsWidth, 0);
}

export function thumbsTranslationValue({offsetWidth, offsetLeft}, scrollerWidth, maxValue) {
  const target = offsetLeft + (offsetWidth / 2);
  return Math.max(Math.min(0, -1 * (target - (scrollerWidth / 2))), maxValue);
}

const PADDLE_DISTANCE = 150;

export class ListingThumbnails extends React.Component<
  ListingThumbnailsProps,
  ListingThumbnailsState
> {
  scroller: React.RefObject<HTMLDivElement>;
  thumbnails: React.RefObject<HTMLUListElement>;
  instanceVisibilityMap: Map<string, ThumbnailVisibilityStatus>;

  constructor(props) {
    super(props);

    this.instanceVisibilityMap = new Map();

    this.scroller = React.createRef();
    this.thumbnails = React.createRef();

    this.state = {
      showLeftArrow: false,
      showRightArrow: false,
      thumbsTranslate: 0,
      maxThumbsTranslate: 0,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const {selected, orientation} = this.props;

    if (this.thumbnails.current === null || this.scroller.current === null) {
      return;
    }

    const maxValue = maxTranslationValue(
      this.scroller.current.offsetWidth,
      this.thumbnails.current.offsetWidth,
    );

    const newState: any = {};

    if (prevState.maxThumbsTranslate !== maxValue) {
      newState.maxThumbsTranslate = maxValue;
    }

    if (prevProps.selected !== selected || prevProps.orientation !== orientation) {
      const activeThumb = this.instanceVisibilityMap.get(selected.id);

      if (activeThumb && activeThumb.component.wrapper.current) {
        const {maxThumbsTranslate} = this.state;
        const thumbsTranslate = thumbsTranslationValue(
          activeThumb.component.wrapper.current.node,
          this.scroller.current.offsetWidth,
          newState.maxThumbsTranslate || maxThumbsTranslate,
        );

        newState.thumbsTranslate = thumbsTranslate;
      }
    }

    if (Object.keys(newState).length > 0) {
      this.setState(newState);
    }
  }

  handleThumbVisibilityChange = (listingThumbnail: ListingThumbnail, inView: boolean) => {
    const {images} = this.props;

    if (listingThumbnail.props.index === 0) {
      this.setState({showLeftArrow: !inView});
    } else if (images.length - 1 === listingThumbnail.props.index) {
      this.setState({showRightArrow: !inView});
    }

    this.instanceVisibilityMap.set(listingThumbnail.props.image.id, {
      props: listingThumbnail.props,
      inView,
      component: listingThumbnail,
    });
  };

  handleRightPaddleClick = () => {
    const {maxThumbsTranslate} = this.state;
    this.setState((prevState) => ({
      thumbsTranslate: Math.max(prevState.thumbsTranslate - PADDLE_DISTANCE, maxThumbsTranslate),
    }));
  };

  handleLeftPaddleClick = () => {
    this.setState((prevState) => ({
      thumbsTranslate: Math.min(prevState.thumbsTranslate + PADDLE_DISTANCE, 0),
    }));
  };

  handleImageClick = (id: string) => {
    const {onThumbnailClick = NOOP, images} = this.props;
    const image = images.find((image) => image.id === id);
    onThumbnailClick(image as ShopScreenShot);
  };

  render() {
    const {showLeftArrow, showRightArrow, thumbsTranslate} = this.state;
    const {images, selected, altPrefix} = this.props;

    return (
      <>
        <section ref={this.scroller} className={style.container}>
          {showLeftArrow && (
            <button
              type="button"
              onClick={this.handleLeftPaddleClick}
              className={classNames(style.arrow, style.left)}
            >
              <Icon icon="chevronLeft" fill="#f7f7f8" size={20} />
            </button>
          )}
          <ul
            ref={this.thumbnails}
            style={{transform: `translate(${thumbsTranslate}px)`}}
            className={style.thumbnails}
          >
            {images.map((image, index) => (
              <ListingThumbnail
                key={image.id}
                alt={`${altPrefix} - ${index + 1}`}
                index={index}
                image={image}
                active={selected === image}
                container={this.scroller}
                onClick={this.handleImageClick}
                onVisibilityChange={this.handleThumbVisibilityChange}
              />
            ))}
          </ul>
          {showRightArrow && (
            <button
              type="button"
              onClick={this.handleRightPaddleClick}
              className={classNames(style.arrow, style.right)}
            >
              <Icon icon="chevronRight" fill="#f7f7f8" size={20} />
            </button>
          )}
        </section>
      </>
    );
  }
}
